import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    useAutoReferralProgram,
    useAutoReferralProgramStats,
    useUpdateAutoReferralProgram,
} from "../../api/autoReferral";
import { AutoReferralProgram } from "../../types/autoReferral";
import { AutoReferralAssignmentsHistory } from "../../components/AutoReferralAssignmentsHistory";

const AutoReferralProgramDetails: React.FC = () => {
    const params = useParams();
    const navigate = useNavigate();
    const programId = params.programId ? Number(params.programId) : null;

    const {
        data: programResponse,
        isLoading,
        isError,
        error,
        refetch: refetchProgram,
    } = useAutoReferralProgram(programId);
    const {
        data: statsResponse,
        isLoading: statsLoading,
        refetch: refetchStats,
    } = useAutoReferralProgramStats(programId);
    const updateProgram = useUpdateAutoReferralProgram();

    const program: AutoReferralProgram | undefined = programResponse?.data;
    const stats = statsResponse?.data;

    const handleToggleSubscriptions = async () => {
        if (!program) {
            return;
        }
        try {
            await updateProgram.mutateAsync({
                id: program.id,
                is_subscription_active: !program.is_subscription_active,
            });
            refetchProgram();
            refetchStats();
        } catch (err: any) {
            window.alert(
                err?.response?.data?.message ||
                    err?.message ||
                    "Не удалось обновить статус подписки."
            );
        }
    };

    if (!programId) {
        return (
            <div className="p-6 text-red-500">Некорректный идентификатор программы.</div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-6 text-gray-500 dark:text-gray-300">
                Загрузка данных программы...
            </div>
        );
    }

    if (isError || !program) {
        return (
            <div className="p-6 text-red-500">
                {(error as any)?.message || "Программа не найдена."}
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <button
                        onClick={() => navigate(-1)}
                        className="mb-3 inline-flex items-center text-sm text-blue-600 hover:text-blue-700 transition"
                    >
                        ← Назад
                    </button>
                    <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
                        {program.name}
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-gray-300 max-w-3xl">
                        {program.short_description || "Краткое описание отсутствует."}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Собрано средств</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {program.total_collected_amount} USD
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                        Всего платежей: {stats?.totals.total_paid_amount ?? program.total_collected_amount} USD
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Участников</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {program.total_participants}
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                        Активных подписок: {stats?.totals.active_subscriptions ?? 0}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Подписки</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats?.totals.active_subscriptions ?? 0}
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                        Всего: {stats?.totals.total_subscriptions ?? 0}
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Управление программой
                    </h2>
                    <div className="flex items-center gap-3">
                        <Link
                            to={`/dashboard/auto-referral-programs/${program.id}/gallery`}
                            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition"
                        >
                            Галерея
                        </Link>
                        <Link
                            to={`/dashboard/auto-referral-programs/${program.id}/subscribers`}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                        >
                            Подписчики и платежи
                        </Link>
                        <button
                            onClick={handleToggleSubscriptions}
                            className={`px-4 py-2 rounded-lg text-white transition ${
                                program.is_subscription_active
                                    ? "bg-red-500 hover:bg-red-600"
                                    : "bg-blue-600 hover:bg-blue-700"
                            }`}
                        >
                            {program.is_subscription_active
                                ? "Остановить списания"
                                : "Возобновить списания"}
                        </button>
                    </div>
                </div>

                <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                    <div>
                        <span className="font-semibold">Реферальный код:</span>{" "}
                        <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded font-mono">
                            {program.referral_code}
                        </code>
                    </div>
                    <div>
                        <span className="font-semibold">Минимальная подписка:</span>{" "}
                        {program.min_subscription_amount} USD
                    </div>
                    <div>
                        <span className="font-semibold">Максимальный коэффициент:</span>{" "}
                        {program.max_coefficient}
                    </div>
                    <div>
                        <span className="font-semibold">Описание:</span>
                        <div className="mt-1 whitespace-pre-wrap">
                            {program.description || "Описание не заполнено."}
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Топ участников
                    </h2>
                    <div className="text-sm text-gray-500">
                        Всего с учётом ручных платежей:{" "}
                        {stats?.totals.total_paid_amount ?? program.total_collected_amount} USD
                    </div>
                </div>
                {statsLoading && (
                    <div className="text-gray-500 dark:text-gray-300">
                        Загрузка статистики...
                    </div>
                )}
                {!statsLoading && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Пользователь
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Сумма взносов
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Процент вложений
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Коэффициент
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                {stats?.top_subscriptions.length ? (
                                    stats.top_subscriptions.map((subscription) => (
                                        <tr key={subscription.id}>
                                            <td className="px-4 py-2">
                                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    {subscription.user?.name ||
                                                        subscription.user?.email ||
                                                        `ID: ${subscription.user_id}`}
                                                </div>
                                                {subscription.user?.email && (
                                                    <div className="text-xs text-gray-500">
                                                        {subscription.user.email}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                                {subscription.total_paid_amount} USD
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                                {subscription.investment_percent.toFixed(2)}%
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                                x{subscription.coefficient}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="px-4 py-3 text-center text-gray-500"
                                        >
                                            Подписчики ещё не оформляли платежи.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <AutoReferralAssignmentsHistory programId={programId} />
        </div>
    );
};

export default AutoReferralProgramDetails;

